import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { X, Maximize2 } from "lucide-react";
import { MenuAPI, CategoriesAPI, resolveImageUrl } from "@/admin/api";
import { useAdmin } from "@/admin/store";
import { BtnPrimary, Confirm, EmptyState, PageHeader, SearchInput, StatusPill, Toggle, selectCls } from "@/components/admin/ui";

const SORTS = [
  { id: "name", label: "Name" },
  { id: "price", label: "Price" },
  { id: "category", label: "Category" },
  { id: "created", label: "Created Date" },
];

const DIETARY_FILTERS = [
  { id: "all", label: "All Dietary" },
  { id: "veg", label: "Vegetarian Only" },
  { id: "non-veg", label: "Non-Veg Only" },
  { id: "featured", label: "Chef's Pick Only" },
  { id: "coastal", label: "Coastal Journey" },
  { id: "pan-indian", label: "Pan-Indian Journey" },
];

const MenuItems = () => {
  const { log } = useAdmin();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [dietary, setDietary] = useState("all");
  const [status, setStatus] = useState("all");
  const [sort, setSort] = useState("name");
  const [confirmDel, setConfirmDel] = useState(null);
  const [previewDish, setPreviewDish] = useState(null);

  const { data: menuItems = [], isLoading: menuLoading } = useQuery({ queryKey: ["menuItems"], queryFn: MenuAPI.getAll });
  const { data: categories = [], isLoading: catLoading } = useQuery({ queryKey: ["categories"], queryFn: CategoriesAPI.getAll });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }) => MenuAPI.update(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries(["menuItems"]);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: MenuAPI.delete,
    onSuccess: () => {
      queryClient.invalidateQueries(["menuItems"]);
      setConfirmDel(null);
    },
  });

  const catName = (id) => categories.find((c) => c.categoryId === id)?.categoryName || "—";

  let rows = menuItems.filter((m) => {
    if (status === "active" && (m.isDeleted || !m.isActive)) return false;
    if (status === "inactive" && (m.isDeleted || m.isActive)) return false;
    if (status === "deleted" && !m.isDeleted) return false;
    if (status === "all" && m.isDeleted) return false;
    if (cat !== "all" && m.categoryId !== Number(cat)) return false;

    if (dietary === "veg" && !m.isVegetarian) return false;
    if (dietary === "non-veg" && m.isVegetarian) return false;
    if (dietary === "featured" && !m.isFeatured) return false;
    if (dietary === "coastal" && m.journey !== "coastal") return false;
    if (dietary === "pan-indian" && m.journey !== "pan-indian") return false;

    return (m.itemName || "").toLowerCase().includes(q.toLowerCase());
  });

  rows = [...rows].sort((a, b) => {
    if (sort === "name") return (a.itemName || "").localeCompare(b.itemName || "");
    if (sort === "price") return a.price - b.price;
    if (sort === "category") return catName(a.categoryId).localeCompare(catName(b.categoryId));
    return b.itemId - a.itemId;
  });

  if (menuLoading || catLoading) return <div className="p-8 text-cream/50">Loading menu...</div>;

  return (
    <div data-testid="admin-menu-items">
      <PageHeader title="THE OONU" mal="ഊണ്" sub="Manage what comes to the table.">
        <BtnPrimary data-testid="add-menu-item-btn" onClick={() => navigate("/admin/menu/new")}>
          + Add Menu Item
        </BtnPrimary>
      </PageHeader>

      <div className="mb-8 flex flex-wrap items-center gap-3">
        <div className="w-full md:w-auto flex-grow max-w-sm">
          <SearchInput value={q} onChange={setQ} placeholder="Search dishes" />
        </div>
        <select data-testid="menu-filter-category" className={`${selectCls} md:w-48`} value={cat} onChange={(e) => setCat(e.target.value)}>
          <option value="all">All categories</option>
          {categories.filter((c) => !c.isDeleted).map((c) => (
            <option key={c.categoryId} value={c.categoryId}>{c.categoryName}</option>
          ))}
        </select>
        <select data-testid="menu-filter-dietary" className={`${selectCls} md:w-44`} value={dietary} onChange={(e) => setDietary(e.target.value)}>
          {DIETARY_FILTERS.map((df) => (
            <option key={df.id} value={df.id}>{df.label}</option>
          ))}
        </select>
        <select data-testid="menu-filter-status" className={`${selectCls} md:w-48`} value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="all">Active & Inactive</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="deleted">Deleted</option>
        </select>
        <select data-testid="menu-sort" className={`${selectCls} md:w-40`} value={sort} onChange={(e) => setSort(e.target.value)}>
          {SORTS.map((s) => (
            <option key={s.id} value={s.id}>Sort: {s.label}</option>
          ))}
        </select>
      </div>

      {rows.length === 0 ? (
        <EmptyState title="NOTHING IN THE POT" line="No dishes match that craving. Clear the filters, or add the first dish to the Oonu." actionLabel="+ Add Menu Item" onAction={() => navigate("/admin/menu/new")} />
      ) : (
        <div className="border-t border-cream/10">
          {rows.map((m) => (
            <div key={m.itemId} data-testid={`menu-row-${m.itemId}`} className="grid grid-cols-1 gap-4 border-b border-cream/10 py-4 md:grid-cols-12 md:items-center">
              <div className="flex items-center gap-4 md:col-span-4">
                <div
                  className={`relative h-14 w-14 shrink-0 overflow-hidden border border-cream/10 ${m.imagePath ? "cursor-pointer hover:border-brass transition-colors group" : ""}`}
                  onClick={() => m.imagePath && setPreviewDish(m)}
                  title={m.imagePath ? "Click to enlarge photograph" : "No photograph"}
                >
                  {m.imagePath ? (
                    <>
                      <img src={resolveImageUrl(m.imagePath)} alt={m.itemName} className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-110" />
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <Maximize2 size={15} className="text-cream" />
                      </div>
                    </>
                  ) : (
                    <div className="flex h-full w-full items-center justify-center bg-bark text-[9px] text-cream/30">No pic</div>
                  )}
                  {m.isFeatured && (
                    <div className="absolute top-0 right-0 bg-brass text-ink font-bold text-[7px] px-1">★</div>
                  )}
                </div>
                <div>
                  <p className="font-display text-base leading-tight text-cream flex items-center gap-1.5">
                    {m.itemName}
                  </p>
                  <p className="mt-0.5 font-body text-[10px] tracking-[0.2em] uppercase text-cream/40">
                    {catName(m.categoryId)}
                  </p>
                </div>
              </div>

              <span className="font-display text-lg italic text-brass md:col-span-1">₹{m.price}</span>

              {/* Dietary & Allergen Badges */}
              <div className="flex flex-wrap items-center gap-1.5 md:col-span-3">
                {m.isVegetarian ? (
                  <span className="border border-emerald-500/60 bg-emerald-500/10 text-emerald-400 text-[8px] font-bold px-1.5 py-0.5 rounded-sm">
                    VEG
                  </span>
                ) : (
                  <span className="border border-cream/15 text-cream/40 text-[8px] px-1.5 py-0.5 rounded-sm">
                    NON-VEG
                  </span>
                )}

                {m.spiceLevel > 0 && (
                  <span className="text-[10px]" title={`Spice: ${m.spiceLevel}`}>
                    {"🌶️".repeat(m.spiceLevel)}
                  </span>
                )}

                {m.containsGluten && (
                  <span className="font-mono text-[8px] font-bold text-amber-300 border border-amber-500/40 bg-amber-500/10 px-1 py-0.5 rounded-sm" title="Contains Gluten">
                    G
                  </span>
                )}
                {m.containsNuts && (
                  <span className="font-mono text-[8px] font-bold text-orange-300 border border-orange-500/40 bg-orange-500/10 px-1 py-0.5 rounded-sm" title="Contains Nuts">
                    N
                  </span>
                )}
                {m.containsDairy && (
                  <span className="font-mono text-[8px] font-bold text-sky-300 border border-sky-500/40 bg-sky-500/10 px-1 py-0.5 rounded-sm" title="Contains Dairy">
                    D
                  </span>
                )}

                {m.journey && (
                  <span className="border border-brass/30 text-brass text-[8px] tracking-wider uppercase px-1.5 py-0.5 rounded-sm">
                    {m.journey === "coastal" ? "Coastal" : "Pan-Indian"}
                  </span>
                )}
              </div>

              <div className="flex items-center gap-3 md:col-span-2">
                <Toggle
                  testId={`menu-toggle-${m.itemId}`}
                  checked={m.isActive}
                  onChange={(v) => {
                    updateMutation.mutate({ id: m.itemId, payload: { ...m, isActive: v } });
                    log(v ? "Dish made active" : "Dish made inactive", m.itemName);
                  }}
                />
                <StatusPill tone={m.isDeleted ? "terra" : m.isActive ? "leaf" : "cream"}>
                  {m.isDeleted ? "Deleted" : m.isActive ? "Active" : "Inactive"}
                </StatusPill>
              </div>

              <div className="flex gap-3 md:col-span-2 md:justify-end">
                <button data-testid={`menu-edit-${m.itemId}`} onClick={() => navigate(`/admin/menu/${m.itemId}/edit`)} className="font-body text-[10px] tracking-[0.2em] uppercase text-brass/80 hover:text-brass">Edit</button>
                {!m.isDeleted ? (
                  <button data-testid={`menu-delete-${m.itemId}`} onClick={() => setConfirmDel(m)} className="font-body text-[10px] tracking-[0.2em] uppercase text-terra/80 hover:text-terra">Delete</button>
                ) : (
                  <button data-testid={`menu-restore-${m.itemId}`} onClick={() => { updateMutation.mutate({ id: m.itemId, payload: { ...m, isDeleted: false } }); log("Dish restored", m.itemName); }} className="font-body text-[10px] tracking-[0.2em] uppercase text-cream/60 hover:text-brass">Restore</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {confirmDel && (
        <Confirm
          testId="menu-delete-confirm"
          title="REMOVE THIS DISH?"
          body={`“${confirmDel.itemName}” will no longer appear in the active Oonu menu. You can restore it later.`}
          onCancel={() => setConfirmDel(null)}
          onConfirm={() => {
            deleteMutation.mutate(confirmDel.itemId);
            log("Dish removed", confirmDel.itemName);
          }}
        />
      )}

      {/* Photo Enlarge Lightbox Modal */}
      {previewDish && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-md p-4"
          onClick={() => setPreviewDish(null)}
        >
          <div
            className="relative max-w-lg w-full bg-bark border border-cream/20 rounded-2xl p-6 text-center shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setPreviewDish(null)}
              aria-label="Close"
              className="absolute top-4 right-4 z-10 h-8 w-8 rounded-full bg-ink/70 hover:bg-terra text-cream flex items-center justify-center transition-colors"
            >
              <X size={16} />
            </button>
            <div className="overflow-hidden rounded-xl border border-cream/15 max-h-[60vh] bg-ink flex items-center justify-center">
              <img
                src={resolveImageUrl(previewDish.imagePath)}
                alt={previewDish.itemName}
                className="max-h-[60vh] w-full object-contain"
              />
            </div>
            <h3 className="mt-4 font-display text-xl text-ivory tracking-tight">{previewDish.itemName}</h3>
            <p className="font-body text-xs text-brass tracking-widest uppercase mt-1">
              {catName(previewDish.categoryId)} • ₹{previewDish.price}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default MenuItems;
